import { Report, ServiceItem } from '../types';
import { getServiceLog } from './serviceJournal';
import { isAccountingEntry } from './serviceLogUtils';
import { inspectionProofMatcher } from './projectLv';
import { istLeistungVomTyp } from './serviceTypeMatch';

/**
 * „Wann ist in diesem Modul zuletzt etwas passiert?" — je Kachel des
 * Projekt-Hubs EIN Zeitstempel. Reine Logik ohne Supabase, damit sie ohne
 * DOM-Testumgebung charakterisierbar ist (moduleActivity.test.ts). Das Laden
 * der Tabellenwerte steht in moduleActivityService.ts.
 */
export type ModuleActivityKey =
    | 'dokumente'
    | 'archiv'
    | 'sendeverlauf'
    | 'planung'
    | 'vorank'
    | 'sigeplan'
    | 'asplan'
    | 'einweisung'
    | 'unterlage'
    | 'bauordnung'
    | 'begehung'
    // SiFa-Raster (SifaModuleGrid): max(updated_at) von sifa_unterweisungen
    // bzw. sifa_mitarbeiter, geladen ueber letzteAktivitaet (services/sifa)
    // und nur bei projectType 'sifa' — SiGeKo-Projekte fragen nicht danach.
    | 'unterweisung'
    | 'mitarbeiter'
    // Nr. 92: max(updated_at) von sifa_fristen bzw. sifa_taetigkeiten.
    | 'fristen'
    | 'taetigkeiten'
    // Nr. 167: max(updated_at) von sifa_gbu (Kachel Gefaehrdungsbeurteilungen).
    | 'gbu';

/** ISO-Zeitstempel oder reines Datum (YYYY-MM-DD); null = noch nichts geschehen. */
export type ModuleActivity = Partial<Record<ModuleActivityKey, string | null>>;

const NUR_DATUM = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * Der spaeteste von mehreren Zeitstempeln. Unparsebare Werte und Luecken
 * werden uebergangen — eine kaputte Altzeile darf die Anzeige nicht leeren.
 */
export const spaetestes = (...werte: (string | null | undefined)[]): string | null => {
    let best: string | null = null;
    let bestMs = Number.NEGATIVE_INFINITY;
    for (const w of werte) {
        if (!w) continue;
        const ms = Date.parse(w);
        if (Number.isNaN(ms)) continue;
        if (ms > bestMs) { bestMs = ms; best = w; }
    }
    return best;
};

/**
 * Anzeige als TT.MM.JJJJ. Ein reines Datum (die `date`-Spalte des Archivs)
 * wird OHNE Zeitzonenumrechnung gesetzt: `new Date('2026-09-07')` ist UTC-
 * Mitternacht und laege westlich von Greenwich am Vortag.
 */
export const formatAktivitaet = (wert: string | null | undefined): string | null => {
    if (!wert) return null;
    const m = NUR_DATUM.exec(wert);
    if (m) return `${m[3]}.${m[2]}.${m[1]}`;
    const d = new Date(wert);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

type ProjektJson = Pick<Report, 'vorankuendigung' | 'unterlageVersions'> & {
    metadata?: { globalData?: Report['metadata']['globalData'] };
};

/**
 * Letzte Buchung auf die Positionen einer Kachel. Der Unterlage-Export legt
 * keine Version an, bucht aber die LV-Position („Unterlage exportiert") —
 * ohne diesen Blick stuende nach Export und Buchung „Noch keine
 * Aktivität". Abrechnungszeilen (BU-/VZ-…) sind keine Leistung und zaehlen
 * nicht; Tochterprojekte buchen auf die Mutter, deren Journal liegt hier
 * nicht vor — dann bleibt es bei den Versionen.
 */
const letzteBuchung = (report: ProjektJson, type: 'unterlage'): string | null => {
    const globalData = report.metadata?.globalData;
    if (!globalData) return null;
    const services: ServiceItem[] = globalData.services || [];
    const ids = new Set(services.filter(s => istLeistungVomTyp(s, type)).map(s => s.id));
    if (ids.size === 0) return null;
    const log = getServiceLog({ metadata: { globalData } }) || [];
    return spaetestes(...log.filter(l => ids.has(l.serviceId) && !isAccountingEntry(l)).map(l => l.date));
};

/**
 * Letzter BEGEHUNGSNACHWEIS auf einer Monatspauschale (Menge 0, 'NW-…').
 *
 * Im Pauschalprojekt steht die Begehung seit 30.09.2026 als Nachweiszeile
 * auf der Pauschale. Die Pauschale selbst ist keine Begehungsposition
 * (istLeistungVomTyp) — ihre Monatsbuchungen sind keine Begehung; ihre
 * Nachweise schon. Ein versendeter Bericht steht ohnehin im Archiv
 * (report_history.date, moduleActivityService); hinzu kommt so der
 * Handnachweis ohne Bericht auf einer reinen Begehungspauschale, den
 * inspectionProofMatcher ebenfalls als Begehung wertet.
 */
const letzterBegehungsnachweis = (report: ProjektJson): string | null => {
    const globalData = report.metadata?.globalData;
    if (!globalData) return null;
    const isProof = inspectionProofMatcher(globalData.services || []);
    const log = getServiceLog({ metadata: { globalData } }) || [];
    return spaetestes(...log.filter(l => isProof(l)).map(l => l.date));
};

/**
 * Module, deren Stand im Projekt-JSON liegt: Vorankuendigung (Verlauf +
 * Versionsarchiv), Unterlage (Versionsstaende + Export-Buchung) und das
 * Archiv, das neben den Begehungsberichten (Tabelle) genau diese beiden
 * JSON-Bestaende zeigt (ProjectArchiveView). Das laufende Tippen im Editor
 * traegt keinen Stempel — gezaehlt wird, was erzeugt, versendet, exportiert
 * oder als Version gesichert wurde.
 */
export const aktivitaetAusProjektJson = (report: ProjektJson): Pick<ModuleActivity, 'vorank' | 'unterlage' | 'archiv' | 'begehung'> => {
    const v = report.vorankuendigung;
    const vorankArchiv = (v?.archive || []).map(a => a.date);
    const unterlageVersionen = (report.unterlageVersions || []).map(u => u.createdAt);
    return {
        vorank: spaetestes(...(v?.history || []).map(h => h.date), ...vorankArchiv),
        unterlage: spaetestes(...unterlageVersionen, letzteBuchung(report, 'unterlage')),
        archiv: spaetestes(...vorankArchiv, ...unterlageVersionen),
        begehung: letzterBegehungsnachweis(report),
    };
};

/** Mehrere Quellen zu einer Karte; je Schluessel gewinnt der spaeteste Wert. */
export const mergeAktivitaet = (...teile: ModuleActivity[]): ModuleActivity => {
    const out: ModuleActivity = {};
    for (const teil of teile) {
        for (const [k, v] of Object.entries(teil) as [ModuleActivityKey, string | null | undefined][]) {
            out[k] = spaetestes(out[k], v);
        }
    }
    return out;
};
